import { Badge } from "@/components/ui/Badge";
import { shortDate } from "@/lib/format";
import { STATUS_LABEL, type StockStatus as Status, type StockStatusFields } from "../lib/stockHealth";

export const BADGE_VARIANT: Record<Status, "danger" | "warning" | "success" | "info" | "outline"> = {
  critical: "danger",
  order_soon: "warning",
  restocking: "outline",
  healthy: "success",
  overstock: "info",
  no_sales: "info",
};

// "Out Oct 28 – Nov 9; a shipment already on the way restocks it." for each
// skipped stockout.
function gapsExplanation(row: StockStatusFields): string {
  const today = new Date().toISOString().slice(0, 10);
  return row.restock_gaps
    .map((gap) => `Out ${shortDate(gap.start_iso, today)} – ${shortDate(gap.end_iso, today)}; a shipment already on the way restocks it.`)
    .join(" ");
}

const RESTOCKING_TEXT = "The orders already on the way are enough, so there is nothing to buy now.";

// Hover text spelling out how the status was reached. Past skipped
// stockouts, the out-of-stock day the status is worked out on is the
// lead time plus the buffer.
function statusExplanation(row: StockStatusFields): string {
  if (row.restock_gaps.length > 0) {
    const then =
      row.buffer_days == null
        ? "Then lasts past the plan."
        : `Then runs out in ${(row.lead_time_days + row.buffer_days).toLocaleString()} days. ${leadText(row)}, ${bufferText(row.buffer_days)}.`;
    return `${gapsExplanation(row)} ${then}${restockingText(row)}`;
  }
  if (row.stockout_after_plan && row.stockout_days != null) {
    return `Lasts past the plan's ${row.stockout_days.toLocaleString()} days, with nothing sold or gifted in the last 90 days to project from after it, so there is no status.`;
  }
  if (row.stockout_days == null || row.buffer_days == null) {
    return "No plan and nothing sold or gifted in the last 90 days, so there is nothing to project from.";
  }
  const source =
    row.stockout_source === "plan"
      ? "on the plan (nothing sold or gifted in the last 90 days)"
      : "at the average sold or gifted per day";
  return `Runs out in ${row.stockout_days.toLocaleString()} days ${source}, counting incoming shipments. ${leadText(row)}, ${bufferText(row.buffer_days)}.${restockingText(row)}`;
}

function restockingText(row: StockStatusFields): string {
  return row.status === "restocking" ? ` ${RESTOCKING_TEXT}` : "";
}

function leadText(row: StockStatusFields): string {
  return `An order placed today arrives in ${row.lead_time_days} days${
    row.lead_time_default ? " (default: no supplier lead time on file)" : ""
  }`;
}

function bufferText(buffer: number): string {
  return buffer >= 0 ? `${buffer} days before stock runs out` : `${-buffer} days after stock runs out`;
}

interface Props {
  row: StockStatusFields;
  // Layout of the badge and its "(Default lead time)" line.
  className?: string;
}

// The Inventory Overview Status: on the page's Status column and in the
// product quick view's header.
export default function StockStatus({ row, className }: Props) {
  // A not-moving product sold nothing, so "Not enough sales data" would say
  // nothing new; it is left blank.
  const hidden = row.status === "no_sales" && row.kind === "not_moving";
  return (
    <div className={className} title={statusExplanation(row)}>
      {row.status === null || hidden ? null : row.status === "no_sales" ? (
        <span className="text-right text-xs">{STATUS_LABEL.no_sales}</span>
      ) : (
        <Badge variant={BADGE_VARIANT[row.status]}>{STATUS_LABEL[row.status]}</Badge>
      )}
      {row.lead_time_default && row.status !== null && row.status !== "no_sales" && (
        <span className="text-xs">(Default lead time)</span>
      )}
    </div>
  );
}
